import * as React from 'react';
import { Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { ContractComparisonModel } from '../../models';
import { ContractComparisonDetailsRenderer, ContractComparisonSummaryRenderer } from '..';
import { CustomDivider } from '../../../custom_shared/components';
import { Utils } from '../../../custom_shared/misc';
import { InstanceUtils, NodeIds, InputIds } from '../../misc';
import { ContractComparisonFilterDropdown } from '..';
import './ContractComparisonRenderer.less';

const tabKeys = {
    summary: 'summary',
    details: 'details'
} as const;

interface Props {
    contractComparison: ContractComparisonModel;
}

const ContractComparisonRenderer: React.FC<Props> = ({ contractComparison }) => {
    const [activeTab, setActiveTab] = React.useState<keyof typeof tabKeys>(() =>
        contractComparison.hasResolvedSummaryForCurrentPayload ? tabKeys.summary : tabKeys.details
    );
    const previousIsSelectedRef = React.useRef(contractComparison.isSelected);

    React.useEffect(() => {
        if (!previousIsSelectedRef.current && contractComparison.isSelected) {
            setActiveTab(
                contractComparison.hasResolvedSummaryForCurrentPayload ? tabKeys.summary : tabKeys.details
            );
        }

        previousIsSelectedRef.current = contractComparison.isSelected;
    }, [contractComparison.isSelected, contractComparison.hasResolvedSummaryForCurrentPayload]);

    const previousPeriodRangeLabel = React.useMemo(() => {
        const generalInformationNode = contractComparison.previousContractNodes.find(
            node => node.id === NodeIds.GeneralInformation
        );

        if (!InstanceUtils.isBasicNode(generalInformationNode)) {
            return null;
        }

        const periodStart = generalInformationNode.getInputById(InputIds.GeneralInformation.PeriodStart)?.value;
        const periodEnd = generalInformationNode.getInputById(InputIds.GeneralInformation.PeriodEnd)?.value;

        const parsedPeriodStart = Utils.formatAbsoluteDateValue(periodStart);
        const parsedPeriodEnd = Utils.formatAbsoluteDateValue(periodEnd);

        if (!parsedPeriodStart || !parsedPeriodEnd) {
            return null;
        }

        return `(${parsedPeriodStart} - ${parsedPeriodEnd})`;
    }, [contractComparison.previousContractNodes]);

    return (
        <div className="contract-comparison-renderer">
            <div className="contract-comparison-renderer-header">
                {previousPeriodRangeLabel && (
                    <span className="previous-period-label">
                        Compare previous {previousPeriodRangeLabel} and new contract to identify key changes.
                    </span>
                )}

                <div className="contract-comparison-renderer-header-tabs">
                    <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as keyof typeof tabKeys)}>
                        <Tabs.TabPane key={tabKeys.summary} tab="Summary" />
                        <Tabs.TabPane key={tabKeys.details} tab="Details" />
                    </Tabs>

                    {activeTab === tabKeys.details && <ContractComparisonFilterDropdown />}
                </div>
            </div>

            <CustomDivider type="horizontal" />

            <ContractComparisonSummaryRenderer
                contractComparison={contractComparison}
                isVisible={activeTab === tabKeys.summary}
            />

            <ContractComparisonDetailsRenderer
                contractComparison={contractComparison}
                isVisible={activeTab === tabKeys.details}
            />
        </div>
    );
};

export default observer(ContractComparisonRenderer);
